/* eslint-disable complexity */
/* eslint-disable react-hooks/exhaustive-deps, no-shadow, react/forbid-component-props */
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dataCollector from 'braintree-web/data-collector';
import threeDSecure from 'braintree-web/three-d-secure';
import { Formik } from 'formik';
import filter from 'lodash/filter';
import upperCase from 'lodash/upperCase';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import {
  PAYMENT_TYPES,
  PAYMENT_METHODS,
  PAYMENT_DOD_TRACKING_CARD_STATUS,
  PAYMENT_DOD_TRACKING_GATEWAY,
} from '../../../config/constants/payment';
import { PAYMENT_TOP_CTA_ID } from '../../../config/setup/setup';
import {
  BUTTON_PLACE_ORDER,
  CARD_TOGGLE_ANOTHER_PRE,
  CARD_TOGGLE_ANOTHER_LINK,
  CARD_TOGGLE_ANOTHER_DATA_QA,
  CARD_TOGGLE_SEE_ALL_CARDS,
  DEFAULT_PAYMENT_ERROR,
  LIABILITY_ERROR,
  BUTTON_BUY_WITH_VIP,
  BECOME_A_VIP,
  VIP_PAY_EXCLUSIVE_DEAL_MESSAGE,
  VIP_WITHOUT_UNAVAILABLE,
} from '../../../config/text/text';
import { trackEvent, updateDod } from '../../../helpers/analytics';
import BraintreeService from '../../../helpers/braintreeService';
import { formatPrice } from '../../../helpers/currency';
import CustomErrorsService from '../../../helpers/customErrorsService';
import ErrorTrackingService from '../../../helpers/errorTrackingService';
import { isWowcherBrand } from '../../../helpers/pages';
import { scrollToPayment } from '../../../helpers/payment';
import { getScreenBreakpoint } from '../../../helpers/screen';
import WalletService from '../../../helpers/walletService';
import ThemeContext from '../../../providers/ThemeProvider';
import Message from '../../_generic/Message';
import { logToSplunk } from '../../_generic/axiosSplunk/axiosSplunk';
import CTAButton from '../../_generic/button/CTAButton';
import PrimaryButton from '../../_generic/button/PrimaryButton';
import ClientOnlyPortal from '../../_generic/portal/ClientOnlyPortal';
import Spinner from '../../_generic/spinner/Spinner';
import useToast from '../../_generic/toast/UseToast';
import BasketSocialCues from '../BasketSocialCues';
import cardComponent from './CardComponent';
import PaymentAgreeTerms from './PaymentAgreeTerms';
import PaymentCardToggle from './PaymentCardToggle';
import PaymentSummary from './PaymentSummary';
import VipButtonContainer from './VipButtonContainer';
import VipMessage from './VipMessage';
import VipMessageWrapper from './VipMessageWrapper';
import VoucherContractDisclamer from './VoucherContractDisclamer';

const PaymentSavedCardBt = ({
  afterError,
  atolProtected,
  basketProducts,
  creditCardOptions,
  currencySymbol,
  customError,
  finalOrderAmount,
  isExclusiveProductInBasket,
  isVipUser,
  isWalletApplied,
  orderComplete,
  orderSummary,
  switchToNewCard,
  threeDSCheckEnabled,
  totalQuantity,
  providerError,
  travelDisableButton,
  vipOptIn = false,
  vipOptInFinalOrderAmount,
  vipPreButtonMessage,
  checkoutData,
  welcomeVipEnabled,
  vipPaywall,
  vipSubscriptionMessage,
  isSecondCheckout,
  isCheckout,
}) => {
  const toast = useToast();
  const theme = useContext(ThemeContext);
  const [paymentInProcess, setPaymentInProcess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dataCollectorInstance, setDataCollectorInstance] = useState(null);
  const [threeDSecureInstance, setThreeDSecureInstance] = useState(null);
  const [defaultCard, setDefaultCard] = useState(null);
  const [cardToken, setCardToken] = useState(null);
  const [fullList, setFullList] = useState(false);
  const [singleCard, setSingleCard] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);
  const FORM_ID = 'form-payment-saved-card-bt';
  const isMobile = getScreenBreakpoint() === 'sm';

  useEffect(() => {
    if (isCheckout) {
      BraintreeService.isPostCheckout = false;
    }
  }, [isCheckout]);

  const defaultCardCheck = () => {
    if (creditCardOptions.cards.length === 1) setSingleCard(true);
    let cardDefault = creditCardOptions.cards.filter((card) => card.default);
    if (cardDefault.length === 0) {
      cardDefault = [
        creditCardOptions.cards[creditCardOptions.cards.length - 1],
      ];
    }
    setCardToken(cardDefault[0].token);
    setDefaultCard(cardDefault[0]);
  };

  const sendPayment = (tokenizedPayload) => {
    BraintreeService.preparePayment({
      basketProducts,
      credit: false,
      dataCollectorInstance,
      isWalletApplied,
      orderSummary,
      paymentMethod: PAYMENT_METHODS.card,
      tokenizationPayload: tokenizedPayload,
    });
  };

  const notifyError = (message) => {
    toast.addToast(message, 'toast-error');
  };

  const handleError = (error, message = DEFAULT_PAYMENT_ERROR) => {
    setIsLoading(false);
    setPaymentInProcess(false);
    notifyError(message);
    ErrorTrackingService.logError(error);
  };

  const handleOrderComplete = (order) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }
    setIsLoading(false);
    orderComplete(order);
  };

  const handleOrderError = (error) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }

    if (error instanceof Error) {
      ErrorTrackingService.logError(error);
    }

    // custom errors
    if (
      error.data &&
      error.data.errors &&
      Object.keys(error.data.errors).length > 0
    ) {
      const customErrors = CustomErrorsService.parseErrors(
        error.data.errors,
        error.config.data || {},
      );
      customError({ errors: customErrors });
    }

    const errorMessage = DEFAULT_PAYMENT_ERROR;
    setIsLoading(false);
    setPaymentInProcess(false);
    notifyError(errorMessage);
    afterError();
  };

  const storeProducts = () => {
    if (!BraintreeService.isPostCheckout) {
      if (!basketProducts) {
        handleOrderError('Products are empty');

        return;
      }
      BraintreeService.products = basketProducts;
    }
  };

  const setupMethods = async (clientInstance) => {
    if (!clientInstance) return;
    try {
      const response = await dataCollector.create({
        client: clientInstance,
        kount: true,
        paypal: true,
      });
      if (response) {
        // At this point, you should access the dataCollectorInstance.deviceData value and provide it
        // to your server, e.g. by injecting it into your form as a hidden input.
        setDataCollectorInstance(response);
        BraintreeService.deviceData = response.deviceData;
      }
    } catch (error) {
      handleError(error, error?.message);
    } finally {
      setIsLoading(false);
    }
  };
  const setupProcessor = async (clientInstance) => {
    if (!clientInstance) return;
    if (threeDSCheckEnabled && finalOrderAmount > 0) {
      if (threeDSecureInstance) threeDSecureInstance.teardown();
      try {
        const response = await threeDSecure.create({
          client: clientInstance,
          version: 2,
        });

        if (response) {
          setThreeDSecureInstance(response);
        }
      } catch (error) {
        handleError(error, error?.message);
      }
    }
    setupMethods(clientInstance);
  };

  const submitPayment = async ({ orderAmount, tokenizedPayload }) => {
    if (threeDSCheckEnabled && orderAmount > 0) {
      try {
        const payload = await threeDSecureInstance.verifyCard({
          amount: orderAmount.toFixed(2).toString(),
          bin: tokenizedPayload.details.bin,
          merchantName: isWowcherBrand() ? 'Wowcher' : 'LivingSocial',
          nonce: tokenizedPayload.nonce,
          onLookupComplete: (data, next) => {
            next();
          },
        });
        if (payload.liabilityShiftPossible && !payload.liabilityShifted) {
          // Liablity possible, but failed challenge
          handleOrderError(LIABILITY_ERROR);
        } else {
          // Either the liability shift is not possible, or the liability shifted
          if (payload.nonce) tokenizedPayload.nonce = payload.nonce;
          sendPayment(tokenizedPayload);
        }
      } catch (error) {
        handleError(error, error?.message);
      }
    } else {
      sendPayment(tokenizedPayload);
    }
  };

  const getCardNonce = async ({ cardToken, orderAmount }) => {
    try {
      const response = await BraintreeService.getNonce(cardToken);
      if (!response.data.target) {
        notifyError(LIABILITY_ERROR);
      }
      const tokenizedPayload = response.data.target;
      submitPayment({ orderAmount, tokenizedPayload });
    } catch (error) {
      handleError(error, error?.message);
    }
  };

  const setCard = (card, index) => {
    setCardToken(card.token);
    setCardIndex(index);
  };

  // not express buy, user already paid by card, has a token
  const expressPay = async (vipOptInValue) => {
    const orderAmount = vipOptInValue
      ? vipOptInFinalOrderAmount
      : finalOrderAmount;
    BraintreeService.vipOptIn = vipOptInValue;
    BraintreeService.totalAmountToPay = orderAmount;
    logToSplunk(
      {
        component: 'PaymentSavedCardBt',
        type: 'Vip status',
        vipOptIn: vipOptInValue,
      },
      'frontend logs',
    );
    logToSplunk(
      {
        checkoutData,
        component: 'PaymentSavedCardBt',
        isWalletAppliedBeforePayment: isWalletApplied,
        orderSummary,
        payment: 'Saved Card Payment',
        totalAmountTopay: orderAmount,
        type: 'payment data sent to payment providers',
        vipOptInFinalOrderAmount,
      },
      'frontend logs',
    );
    if (!BraintreeService.isPostCheckout) scrollToPayment();
    trackEvent('card_payment_click');
    if (welcomeVipEnabled && vipOptInValue) trackEvent('vip_cardbw_click');
    if (welcomeVipEnabled && !vipOptInValue) trackEvent('vip_cardbwo_click');
    updateDod({ card_status: PAYMENT_DOD_TRACKING_CARD_STATUS.existing });
    updateDod({ gateway: PAYMENT_DOD_TRACKING_GATEWAY.braintree });
    setPaymentInProcess(true);
    setIsLoading(true);
    BraintreeService.walletUsed = isWalletApplied;
    BraintreeService.postageDiscount = orderSummary.totalPostageDiscount;
    storeProducts();
    providerError({});

    try {
      const response = await WalletService.getWallet();
      const userWallets = filter(
        response.data,
        (wallet) => wallet.currency === upperCase(orderSummary.currency),
      );
      logToSplunk(
        {
          component: 'PaymentSavedCardBt',
          type:
            'Wallet Amount from wallet service setting braintree service wallet amount',
          userWallets,
        },
        'frontend logs',
      );
      BraintreeService.walletAmount = userWallets[0].balance;
      getCardNonce({ cardToken, orderAmount });
    } catch (error) {
      handleOrderError(error);
    }
  };

  const handleSwitchToNewCard = () => {
    if (fullList || singleCard) {
      switchToNewCard();
    }
    setFullList(true);
  };

  const setupBraintree = async () => {
    // payment component will be disabled if user is not logged in
    defaultCardCheck();
    BraintreeService.paymentComplete = handleOrderComplete;
    BraintreeService.paymentError = handleOrderError;
    BraintreeService.paymentType = PAYMENT_TYPES.card;
    BraintreeService.totalQuantity = totalQuantity;
    BraintreeService.providerError = providerError;
    try {
      const clientInstance = await BraintreeService.getClientInstance();

      setupProcessor(clientInstance);
    } catch (error) {
      handleError(error, error?.message);
    }
  };

  useEffect(() => {
    BraintreeService.handleError = handleOrderError;
    BraintreeService.finalOrderAmount = finalOrderAmount;

    setIsLoading(true);
    setupBraintree();
  }, [finalOrderAmount]);

  const renderButton = (isValid) => (
    <CTAButton
      className="full-width"
      disabled={travelDisableButton || paymentInProcess || !isValid}
      formId={FORM_ID}
      onClick={() => expressPay(vipOptIn)}
      type="submit"
    >
      <FontAwesomeIcon
        icon={faLock}
        style={{ height: 13, marginRight: '4px', width: 13 }}
      />
      {vipPaywall
        ? BECOME_A_VIP
        : BUTTON_PLACE_ORDER[process.env.NEXT_PUBLIC_SITE || 'wowcher']}
    </CTAButton>
  );

  const renderWelcomeVipButtons = (isValid) => (
    <>
      <VipButtonContainer big={!vipSubscriptionMessage}>
        <PrimaryButton
          className="less-rounded size-xlarger"
          disabled={travelDisableButton || paymentInProcess || !isValid}
          onClick={() => expressPay(true)}
          type="submit"
        >
          <FontAwesomeIcon
            icon={faLock}
            style={{ height: 18, marginRight: '8px', width: 18 }}
          />
          {BUTTON_BUY_WITH_VIP} <span className="pipe">&nbsp;|&nbsp;</span>{' '}
          {currencySymbol}
          {formatPrice(vipOptInFinalOrderAmount, true, true, false)}
        </PrimaryButton>
        {vipSubscriptionMessage}
      </VipButtonContainer>
      {isExclusiveProductInBasket && (
        <VipButtonContainer>
          <Message message={VIP_PAY_EXCLUSIVE_DEAL_MESSAGE} />
        </VipButtonContainer>
      )}
      {!isExclusiveProductInBasket && (
        <VipButtonContainer>
          <PrimaryButton
            ariaDisabled={isExclusiveProductInBasket}
            ariaDisabledReason={VIP_WITHOUT_UNAVAILABLE}
            className="less-rounded light-blue-color size-xlarger"
            disabled={travelDisableButton || paymentInProcess || !isValid}
            onClick={() => expressPay(false)}
            type="submit"
          >
            <FontAwesomeIcon
              icon={faLock}
              style={{ height: 16, marginRight: '8px', width: 16 }}
            />
            {BUTTON_PLACE_ORDER[process.env.NEXT_PUBLIC_SITE || 'wowcher']}
            <span className="pipe">&nbsp;|&nbsp;</span> {currencySymbol}
            {formatPrice(finalOrderAmount, true, true, false)}
          </PrimaryButton>
        </VipButtonContainer>
      )}
      <VipMessage />
    </>
  );

  return (
    <>
      {isLoading && <Spinner isFullScreen />}
      <Formik
        enableReinitialize
        initialValues={{
          confirmPayment: true,
        }}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
        }}
        validate={(values) => {
          const errors = {};
          if (!values.confirmPayment) {
            errors.confirmPayment = true;
          }

          return errors;
        }}
      >
        {({
          isValid,
          handleSubmit,
          /* and other goodies */
        }) => (
          <form id={FORM_ID} name="payment" onSubmit={handleSubmit}>
            <div className="row">
              <div className="form-group col-12">
                <PaymentCardToggle
                  dataQa={CARD_TOGGLE_ANOTHER_DATA_QA}
                  linkAction={handleSwitchToNewCard}
                  linkText={
                    fullList || singleCard
                      ? CARD_TOGGLE_ANOTHER_LINK
                      : CARD_TOGGLE_SEE_ALL_CARDS
                  }
                  preLinkText={CARD_TOGGLE_ANOTHER_PRE}
                />
              </div>
            </div>
            {(defaultCard || singleCard) && !fullList && (
              <div className="row payment-body active">
                {cardComponent(defaultCard)}
              </div>
            )}
            {((!defaultCard && !singleCard) || fullList) &&
              creditCardOptions?.cards?.map((card, index) => (
                <div
                  aria-hidden="true"
                  className={`row payment-body ${
                    cardIndex === index ? 'active' : ''
                  }`}
                  key={card.token}
                  onClick={() => setCard(card, index)}
                >
                  {cardComponent(card)}
                </div>
              ))}

            <div className="row payment-body">
              <div className="col-12">
                <PaymentAgreeTerms disabled={false} />
                {!isMobile && !vipPaywall && (
                  <BasketSocialCues
                    atolProtected={atolProtected}
                    className="with-margin"
                    isLighter
                    isPostCheckout={BraintreeService.isPostCheckout}
                  />
                )}
                <VoucherContractDisclamer />
                <PaymentSummary
                  {...orderSummary}
                  isPostCheckout={BraintreeService.isPostCheckout}
                  totalQuantity={totalQuantity}
                />
              </div>
            </div>
            <div className="vip-message-shim">
              {!isVipUser && welcomeVipEnabled && vipPreButtonMessage && (
                <VipMessageWrapper>{vipPreButtonMessage}</VipMessageWrapper>
              )}
            </div>
            <div className="row payment-body payment-buttons">
              <div className="col-12">
                {!isVipUser &&
                  welcomeVipEnabled &&
                  renderWelcomeVipButtons(isValid)}
                {(isVipUser || !welcomeVipEnabled) && (
                  <>
                    {renderButton(isValid)}
                    <ClientOnlyPortal targetElementId={PAYMENT_TOP_CTA_ID}>
                      {renderButton(isValid)}
                    </ClientOnlyPortal>
                  </>
                )}
              </div>
              {isSecondCheckout && vipSubscriptionMessage && (
                <div className="col-12">{vipSubscriptionMessage}</div>
              )}
              <div className="col-12">
                {isMobile && !vipPaywall && (
                  <BasketSocialCues
                    atolProtected={atolProtected}
                    className="with-margin"
                    isLighter
                    isPostCheckout={BraintreeService.isPostCheckout}
                  />
                )}
              </div>
            </div>
          </form>
        )}
      </Formik>
      <style jsx>{`
        .active {
          background: #c8e7f4;
          border-radius: 5px;
          color: #949494;
        }
        .vip-message-shim {
          margin: 0 -15px;
        }
        .voucher-contract-disclamer {
          font-size: 11px;
          margin: 5px;
          text-align: justify;
        }
        .payment-body {
          cursor: pointer;
          padding: 2.5% 0.5%;
          margin: 1%;
        }
        .payment-buttons {
          margin: -6%;
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          .payment-buttons {
            margin: 0%;
          }
        }
      `}</style>
    </>
  );
};

PaymentSavedCardBt.propTypes = {
  afterError: PropTypes.func.isRequired,
  atolProtected: PropTypes.bool,
  basketProducts: PropTypes.array.isRequired,
  checkoutData: PropTypes.object,
  creditCardOptions: PropTypes.object.isRequired,
  currencySymbol: PropTypes.string,
  customError: PropTypes.func.isRequired,
  finalOrderAmount: PropTypes.number.isRequired,
  isExclusiveProductInBasket: PropTypes.bool,
  isSecondCheckout: PropTypes.bool,
  isVipUser: PropTypes.bool,
  isWalletApplied: PropTypes.bool,
  orderComplete: PropTypes.func.isRequired,
  orderSummary: PropTypes.object.isRequired,
  providerError: PropTypes.func.isRequired,
  switchToNewCard: PropTypes.func.isRequired,
  threeDSCheckEnabled: PropTypes.bool,
  totalQuantity: PropTypes.number,
  travelDisableButton: PropTypes.bool,
  vipOptIn: PropTypes.bool,
  vipOptInFinalOrderAmount: PropTypes.number,
  vipPaywall: PropTypes.bool,
  vipPreButtonMessage: PropTypes.element,
  vipSubscriptionMessage: PropTypes.element,
  welcomeVipEnabled: PropTypes.bool,
};

export default PaymentSavedCardBt;
